import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { useIntl } from 'react-intl';

import { useVoiceWorkspace } from './useVoiceWorkspace';
import { voiceWorkspaceMessages as messages } from './workspace.messages';

/**
 * Collapsed workspace: a bottom-centre pill shown while the overlay is
 * minimised, so the app (session, created documents) is fully visible with the
 * draft (or the info feed) one tap away. Renders nothing unless a workspace
 * exists, is minimised, and has content to come back to.
 */
export function WorkspaceMinimizedPill() {
  const intl = useIntl();
  const { workspace, infoCards, restore, closeWorkspace } = useVoiceWorkspace();

  if (!workspace || !workspace.minimized) return null;
  const component = workspace.component;
  const latestCard = infoCards.length > 0 ? infoCards[infoCards.length - 1] : null;

  const infoBoardTitle =
    latestCard?.kind === 'generic'
      ? latestCard.title
      : latestCard?.kind === 'evidence'
        ? intl.formatMessage(messages.evidenceCardAriaLabel)
        : null;
  const title =
    component?.kind === 'draft-document'
      ? component.title
      : component?.kind === 'info-board'
        ? infoBoardTitle
        : null;
  if (!title) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[90] flex justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
      data-testid="workspace-minimized-pill"
    >
      <div
        className={cn(
          'pointer-events-auto flex items-center gap-1 rounded-2xl border border-border/60',
          'bg-surface/95 p-1.5 shadow-xl backdrop-blur-md'
        )}
      >
        <button
          type="button"
          onClick={restore}
          aria-label={intl.formatMessage(messages.restore)}
          className={cn(
            'flex min-w-0 items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-start',
            'hover:bg-surface-hover',
            focusRing
          )}
          data-testid="workspace-restore"
        >
          <span className="flex size-6 shrink-0 items-center justify-center">
            <HeidiLogoMark className="size-3.5 text-text-secondary" />
          </span>
          <span className="flex min-w-0 flex-col">
            <TypographyP2Bold className="truncate">{title}</TypographyP2Bold>
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage(messages.restoreHint)}
            </TypographyCaption>
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={intl.formatMessage(messages.close)}
          onClick={closeWorkspace}
          className="rounded-lg text-text-tertiary hover:text-text-primary"
          data-testid="workspace-minimized-close"
        >
          <X />
        </Button>
      </div>
    </div>
  );
}
